import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import useAuth from "../hooks/useAuth";
import { useCompanionGuide } from "../hooks/useCompanionGuide";
import { useProactiveInsights } from "../hooks/useProactiveInsights";
import { useSpaces } from "./SpaceProvider";
import { useLocation } from "react-router-dom";
import type { UserStage, CompanionMessage, DiscoveryCelebration } from "../lib/companionGuide";
import type { ProactiveInsight } from "../api/proactiveInsights";

// ─── Types ───────────────────────────────────────────────────────────────────

interface CompanionContextValue {
  // FE guidance
  messages: CompanionMessage[];
  dismissMessage: (id: string) => void;
  userStage: UserStage;
  userName: string;

  // BE proactive insights
  proactiveInsights: ProactiveInsight[];
  currentFloatingInsight: ProactiveInsight | null;
  unreadInsightCount: number;
  markInsightSeen: (id: string) => void;
  dismissInsight: (id: string) => void;
  hasProactiveInsights: boolean;

  // Discovery
  discoveries: Record<string, number>;
  pendingDiscovery: DiscoveryCelebration | null;
  clearDiscovery: () => void;
  triggerCelebration: (eventKey: string) => void;

  // First login
  showFirstLogin: boolean;
  dismissFirstLogin: () => void;

  // Tab hint — lets child components (e.g. Portal) inform the provider
  // of internal tab changes that don't alter the URL
  setActiveTab: (tab: string) => void;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const CompanionContext = createContext<CompanionContextValue | null>(null);

const EMPTY_COMPANION: CompanionContextValue = {
  messages: [],
  dismissMessage: () => {},
  userStage: "brand_new",
  userName: "",
  proactiveInsights: [],
  currentFloatingInsight: null,
  unreadInsightCount: 0,
  markInsightSeen: () => {},
  dismissInsight: () => {},
  hasProactiveInsights: false,
  discoveries: {},
  pendingDiscovery: null,
  clearDiscovery: () => {},
  triggerCelebration: () => {},
  showFirstLogin: false,
  dismissFirstLogin: () => {},
  setActiveTab: () => {},
};

export const useCompanion = (): CompanionContextValue => {
  const context = useContext(CompanionContext);
  // Return empty defaults when outside provider (e.g. unauthenticated pages)
  return context || EMPTY_COMPANION;
};

// ─── Route Key Resolution ────────────────────────────────────────────────────

function resolveRouteKey(pathname: string, portalTab?: string): string {
  if (pathname.startsWith("/marketplace")) return "marketplace";
  if (pathname === "/portal") {
    if (portalTab === "my_workspace") return "portal_workspace";
    if (portalTab === "activities") return "portal_activities";
    return "";
  }
  if (pathname === "/spaces") return "spaces";
  if (pathname === "/examples") return "examples";
  if (pathname.startsWith("/activities/run")) return "activity_runner";
  return "";
}

// ─── Provider ────────────────────────────────────────────────────────────────

interface CompanionProviderProps {
  children: React.ReactNode;
}

export const CompanionProvider: React.FC<CompanionProviderProps> = ({
  children,
}) => {
  const { auth } = useAuth();
  const location = useLocation();
  const { hasSpaces } = useSpaces();
  const isAuthenticated = !!auth?.accessToken;

  // Internal tab state — updated by child components via setActiveTab
  const [activeTab, setActiveTabState] = useState<string>(
    () => localStorage.getItem("portal_active_tab") || "activities"
  );

  const setActiveTab = useCallback((tab: string) => {
    setActiveTabState(tab);
  }, []);

  // Resolve current route key using React state (not raw localStorage)
  const routeKey = resolveRouteKey(location.pathname, activeTab);

  // BE proactive insights (fetch only when authenticated)
  const proactive = useProactiveInsights(isAuthenticated);

  // Process count — PresListContext processes are currently disabled;
  // will be enhanced when the API is re-enabled
  const processCount = 0;

  // FE companion guide
  const guide = useCompanionGuide(
    routeKey,
    processCount,
    hasSpaces,
    proactive.hasInsights
  );

  // Listen for custom celebration events dispatched from hooks/components
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key) {
        guide.triggerCelebration(detail.key);
      }
    };
    window.addEventListener("clary:celebration", handler);
    return () => window.removeEventListener("clary:celebration", handler);
  }, [guide.triggerCelebration]);

  const value: CompanionContextValue = {
    // FE guidance
    messages: guide.messages,
    dismissMessage: guide.dismissMessage,
    userStage: guide.userStage,
    userName: guide.userName,

    // BE proactive insights
    proactiveInsights: proactive.insights,
    currentFloatingInsight: proactive.currentFloatingInsight,
    unreadInsightCount: proactive.unreadCount,
    markInsightSeen: proactive.markSeen,
    dismissInsight: proactive.dismiss,
    hasProactiveInsights: proactive.hasInsights,

    // Discovery
    discoveries: guide.discoveries,
    pendingDiscovery: guide.pendingDiscovery,
    clearDiscovery: guide.clearDiscovery,
    triggerCelebration: guide.triggerCelebration,

    // First login
    showFirstLogin: guide.showFirstLogin,
    dismissFirstLogin: guide.dismissFirstLogin,

    // Tab hint
    setActiveTab,
  };

  // Don't render companion for unauthenticated users
  if (!auth?.accessToken) {
    return <>{children}</>;
  }

  return (
    <CompanionContext.Provider value={value}>
      {children}
    </CompanionContext.Provider>
  );
};

export default CompanionContext;
